/**
 * accountGroupMemberList.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails, Button, CircularProgress } from "@mui/material"

import { AuthContext } from "../auth/auth.context";
import { MemberTypeDropdown } from "../components/memberTypeDropdown.component";

import { Device, Membership } from "../models/auth";
import { GroupMembership, GroupMemberType, GroupSubmissionType } from "../models/group";
import { authServiceFactory } from "../services/auth.service";
import { groupServiceFactory } from "../services/group.service";

interface MembershipSelection {
  groupMember: GroupMembership,
  acctMember: Membership | undefined | null, // assume account member may have been removed
  newMemberType: GroupMemberType,
  loadingDevices: boolean,
  expanded: boolean,
  devices: Array<Device>
}

interface Props {
  accountID: string,
  groupID: string,
  isDM: boolean,
  submissiontype: GroupSubmissionType,
  onError(error: Error): void
}
export function AccountGroupMembersComponent(props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [curGroupMembers, setCurGroupMembers] = React.useState<MembershipSelection[]>([]);
  const [deviceMap, setDeviceMap] = React.useState<{[userID: string]: Device[]}>({});
  const [memberTypeMenuAnchorEl, setMemberTypeMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedGroupMemberSel, setSelectedGroupMemberSel] = React.useState<MembershipSelection | null>(null);

  React.useEffect(function () {
    groupServiceFactory().getGroupMembers(props.accountID, props.groupID)
    .then(groupMembers => {
      const userIDs: Set<string> = new Set(groupMembers.map(m => m.userID));
      return authServiceFactory().membersFor(userIDs).then(acctMemberMap => {
        return {groupMembers, acctMemberMap};
      });
    }).then(({groupMembers, acctMemberMap}) => {
      const arr: Array<MembershipSelection> = [];
      for (const mem of groupMembers) {
        arr.push({
          groupMember: mem,
          acctMember: acctMemberMap[mem.userID],
          newMemberType: mem.groupMemberType,
          loadingDevices: false,
          expanded: false,
          devices: []
        });
      }
      setCurGroupMembers(arr);
    } , err => {
      console.error("error fetching group members:", err);
      props.onError(err);
    });
  }, [props.accountID, props.groupID]);

  async function changedAccordion(groupMemberSel: MembershipSelection,
      _index: number, expanded: boolean): Promise<any> {
    groupMemberSel.expanded = expanded;
    if (!expanded) {
      setCurGroupMembers(Array.from(curGroupMembers));
      return;
    }
    const userID = groupMemberSel.groupMember.userID;
    if (deviceMap[userID]) { return; }

    groupMemberSel.loadingDevices = true;
    // we fetch new members' devices immediately, so this must be a current member
    setCurGroupMembers(Array.from(curGroupMembers));
    try {
      await fetchDevices(groupMemberSel);
    } catch (err) {
      console.error("Error loading member devices:", err);
      props.onError(err as Error);
    } finally {
      groupMemberSel.loadingDevices = false;
      setCurGroupMembers(Array.from(curGroupMembers));
    }
  }

  async function fetchDevices(groupMemberSel: MembershipSelection): Promise<Array<Device>> {
    const userID = groupMemberSel.groupMember.userID;
    const memberDevices = await authServiceFactory().getMemberDevices(userID);
    const devices = memberDevices.sort(function (a, b): number {
      if (a.modified?.isBefore(b.modified)) {
        return 1;
      } else if (b.modified?.isBefore(a.modified)) {
        return -1;
      }
      return 0;
    });
    groupMemberSel.devices = devices;
    const participatingSet = new Set<string>();
    for (const devSel of groupMemberSel.groupMember.devices) {
      participatingSet.add(devSel._id);
    }
    const participating = devices.filter(d => participatingSet.has(d._id));
    setDeviceMap({
      ...deviceMap,
      [userID]: participating
    });
    return devices;
  }

  async function removeMember(groupMemberSel: MembershipSelection, index: number) {
    if (!authContext.membership) { return; }
    try {
      groupServiceFactory().deleteGroupMemberForUser(
        props.accountID,
        props.groupID,
        groupMemberSel.groupMember.userID);
      
      const arr = Array.from(curGroupMembers);
      arr.splice(index, 1);
      setCurGroupMembers(arr);
    } catch  (err) {
      console.error("Error removing user:", err);
      props.onError(err as Error);
    }
  }

  async function removeDeviceFromGroup(groupMemberSel: MembershipSelection, device: Device) {
    if (!authContext.membership) { return; }
    try {
      groupServiceFactory().deleteGroupMemberForUser(
        props.accountID,
        props.groupID,
        groupMemberSel.groupMember.userID,
        device._id);
      
      const index = groupMemberSel.groupMember.devices.findIndex(d => d._id == device._id);
      if (index < 0) { return; }
      groupMemberSel.groupMember.devices.splice(index, 1);
      if (groupMemberSel.groupMember.devices.length <= 0) {
        const arr = Array.from(curGroupMembers);
        const i = arr.findIndex(g => g.groupMember.userID == groupMemberSel.groupMember.userID);
        if (i >= 0) {
          arr.splice(i, 1);
          setCurGroupMembers(arr);
        }
        return;
      }

      const participatingSet = new Set<string>();
      for (const devSel of groupMemberSel.groupMember.devices) {
        participatingSet.add(devSel._id);
      }
      const participating = groupMemberSel.devices.filter(d => participatingSet.has(d._id));
      setDeviceMap({
        ...deviceMap,
        [groupMemberSel.groupMember.userID]: participating
      });
    } catch (err) {
      console.error("Error removing user:", err);
      props.onError(err as Error);
    }
  }

  async function setMemberType(groupMemberType: GroupMemberType) {
    if (!selectedGroupMemberSel) { return; }
    const member = selectedGroupMemberSel;
    selectedGroupMemberSel.newMemberType = groupMemberType;
    setMemberTypeMenuAnchorEl(null);
    setSelectedGroupMemberSel(null);

    if (!authContext.membership) { return; }
    try {
      await groupServiceFactory().updateGroupMembershipType(
        authContext.membership.accountID,
        props.groupID, member.groupMember.userID,
        member.groupMember.groupname, groupMemberType);
    } catch (err) {
      console.error("Error updating member type:", err);
      props.onError(err as Error);
    }
  }

  function doOpenMemberTypeMenu(event: React.MouseEvent<HTMLButtonElement>, groupMemberSel: MembershipSelection) {
    event.stopPropagation();
    event.preventDefault();
    if (selectedGroupMemberSel && selectedGroupMemberSel === groupMemberSel) {
      setMemberTypeMenuAnchorEl(null);
      setSelectedGroupMemberSel(null);
    } else {
      setMemberTypeMenuAnchorEl(event.currentTarget);
      setSelectedGroupMemberSel(groupMemberSel);
    }
  }

  return <>
    {curGroupMembers.map((memberSel, index) =>
    <Accordion disableGutters elevation={0} square={true} key={memberSel.groupMember.userID}
        className="group-member-accordion" data-user-id={memberSel.groupMember.userID}
        onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
          changedAccordion(memberSel, index, expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <header className="group-member-header"
            data-user-id={memberSel.groupMember.userID}>
          {memberSel.acctMember ? 
            <h4>{memberSel.acctMember.firstname} {memberSel.acctMember.lastname}</h4> :
            <h4>Former Member: {memberSel.groupMember.userID}</h4>}
          <div className="right-controls">
          {props.isDM ?
            <span className="group-member-type">{memberSel.newMemberType}</span> :
            <Button
                disableRipple disableTouchRipple
                onClick={e => doOpenMemberTypeMenu(e, memberSel)}>
              {memberSel.newMemberType}
            </Button>
          }
          {memberSel.expanded &&
            <Button disableRipple disableTouchRipple onClick={() => removeMember(memberSel, index)}>
              Remove All
            </Button>}
          </div>
        </header>
      </AccordionSummary>
      <AccordionDetails>
        <AccountGroupMemberDevices key={memberSel.groupMember.userID}
          groupMember={memberSel}
          loadingDevices={memberSel.loadingDevices}
          devices={deviceMap[memberSel.groupMember.userID]}
          index={index}
          displayTZ={authContext.tz()}
          onRemoveDevice={removeDeviceFromGroup}
          />
      </AccordionDetails>
    </Accordion>)}
    <MemberTypeDropdown
      anchor={memberTypeMenuAnchorEl}
      currentMemberType={selectedGroupMemberSel?.newMemberType}
      currentSubmissionType={props.submissiontype}
      setMemberType={setMemberType}
      onClose={() => { setMemberTypeMenuAnchorEl(null); setSelectedGroupMemberSel(null); }} />
  </>;
}

interface DeviceProps {
  loadingDevices: boolean,
  index: number,
  groupMember: MembershipSelection,
  devices: Array<Device>,
  displayTZ: string,
  onRemoveDevice(groupMember: MembershipSelection, device: Device): void
}
export function AccountGroupMemberDevices(props: DeviceProps): React.ReactElement<any, any> {
  return <article className="group-member-device-list-container">
    {props.loadingDevices ?  <CircularProgress size={20} /> :
    props.devices?.map((device) => {
      const displayString = device.displayString || device._id;
      
      if (props.displayTZ) {
        device.modified?.tz(props.displayTZ);
      }

      return <div className="group-member-device-container flex"
          key={device._id} data-device-id={device._id}>
        <div>
          <h5 className="device-name">{displayString}</h5>
          {device.modified && <span className="desc">Last Seen: {device.modified.format("MMM D, YYYY h:mma")}</span>}
        </div>

        <div className="right-controls">
          <Button onClick={() => props.onRemoveDevice(props.groupMember, device)}>Remove</Button>
        </div>
      </div>;
    })  }
  </article>;
}
